import type { Suggestion as SearchSuggestion } from '~/components/SearchSuggest';
import type {
	Suggestion,
	SuggestionGroup
} from '~/composables/useLocationAutocomplete';
import type { Ref } from 'vue';

import HistoryGroupIcon from '~icons/material-symbols-light/history';

export interface UseFirstFieldSuggestOptions {
	searchTerm: Ref<string>;
	locationAutocompleteData: Ref<SuggestionGroup[] | null | undefined>;
	filteredLastResearchs: Ref<SearchSuggestion[]>;
	searchByCodeOption?: Ref<SearchSuggestion[]>;
}

export interface UseFirstFieldSuggestReturn {
	filteredSuggestions: ComputedRef<SuggestionGroup[]>;
}

/**
 * Composable para gerenciar a lógica do primeiro campo de suggest
 * Filtra sugestões para exibir apenas regiões, cidades e códigos
 */
export function useFirstFieldSuggest({
	searchTerm,
	locationAutocompleteData,
	filteredLastResearchs,
	searchByCodeOption
}: UseFirstFieldSuggestOptions): UseFirstFieldSuggestReturn {
	const hasMinSearchTerm = computed(() => searchTerm.value.length >= 3);

	/**
	 * Filtra e ajusta as sugestões baseado no número de caracteres
	 */
	const filteredSuggestions = computed<SuggestionGroup[]>(() => {
		const groups: SuggestionGroup[] = [];

		// Verificar se há resultados de autocomplete
		const hasAutocompleteResults =
			locationAutocompleteData.value &&
			locationAutocompleteData.value.length > 0 &&
			locationAutocompleteData.value.some(
				group => group.suggestions && group.suggestions.length > 0
			);

		// Mostrar histórico quando:
		// 1. Há menos de 3 caracteres, OU
		// 2. Há 3+ caracteres mas não há resultados de autocomplete
		const shouldShowHistory =
			searchTerm.value.length < 3 || !hasAutocompleteResults;

		// Adicionar histórico se houver itens e atender às condições
		if (filteredLastResearchs.value.length > 0 && shouldShowHistory) {
			groups.push({
				heading: {
					text: 'Últimas buscas',
					icon: HistoryGroupIcon
				},
				key: 'lastResearchs' as any,
				visible: true,
				suggestions: filteredLastResearchs.value as any[]
			});
		}

		// Se não tem dados de localização, retornar apenas histórico
		if (!locationAutocompleteData.value) {
			return groups;
		}

		// Filtrar grupos baseado no número de caracteres
		const filteredLocationGroups = locationAutocompleteData.value.filter(
			group => {
				// Sempre exibir macroregions
				if (group.key === 'macroregions') {
					return true;
				}

				// Cities e code apenas com >= 3 caracteres
				if (group.key === 'cities') {
					return hasMinSearchTerm.value;
				}

				// Ocultar outros grupos (streets, pointsOfInterest, condos, regions/neighborhoods)
				return false;
			}
		);

		// Ajustar limite de cidades para 6
		const adjustedGroups = filteredLocationGroups.map(group => {
			if (group.key === 'cities') {
				return {
					...group,
					suggestions: group.suggestions.slice(0, 6)
				};
			}
			return group;
		});

		// Adicionar grupos de localização filtrados
		groups.push(...adjustedGroups);

		// Adicionar grupo de código se disponível e >= 3 caracteres
		if (
			hasMinSearchTerm.value &&
			searchByCodeOption?.value &&
			searchByCodeOption.value.length > 0
		) {
			groups.push({
				heading: {
					text: 'Código',
					icon: null as any
				},
				key: 'code' as any,
				visible: true,
				suggestions: searchByCodeOption.value as any[]
			});
		}

		return groups;
	});

	return {
		filteredSuggestions
	};
}
